import { useEffect, useMemo, useState } from "react";
import {
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Skeleton } from "@mui/material";
import { EcommerceOrder } from "@/pages/apps/applications/ECommerceApi";
import { Delete, Download, Edit, RemoveRedEye } from "@mui/icons-material";

type Application = EcommerceOrder & {
  application_number?: string;
  application_status?: string;
  apply_date?: string;
  applicant?: {
    id: number;
    name: string;
    email: string;
  };
  pdf_link?: string;
  excel_link?: string;
  is_enabled?: number;
};
import { toast } from "react-toastify";
import ConfirmationDialog from "@/pages/apps/users/Dialog/ConfirmationDialog";
import useUser from "@auth/useUser";
import ChangeStatusMessageModal from "./ChangeStatusMessageModal";
import {
  useDeleteApplicationMutation,
  useGetApplicationsListQuery,
  useUpdateApplicationStatusMutation,
} from "@/store/services/applications";
import { application_statuses } from "./constants/data";
import ApplicationsStatus from "./ApplicationsStatus";
import clsx from "clsx";
import { setIsApplicationsLoading } from "@/components/theme-layouts/components/navbar/navbarSlice";
import { useDispatch } from "react-redux";

function ApplicationsTable({
  isDashboard = false,
  isView = false,
}: {
  limit?: number;
  isDashboard?: boolean;
  isView?: boolean;
}) {
  const { data: user } = useUser();
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<{
    pdf_link: string;
    excel_link: string;
  } | null>(null);

  const {
    data: applicationsData = [],
    isLoading,
    isFetching,
  } = useGetApplicationsListQuery({
    refetchOnMountOrArgChange: true,
  });

  const [removeOrders, { isLoading: isDeleting }] =
    useDeleteApplicationMutation();

  const orders = useMemo(
    () => applicationsData?.data ?? [],
    [applicationsData?.data]
  );

  const isApplicant = useMemo(() => {
    return user?.role === "Applicant";
  }, [user?.role]);

  const handleDelete = (id: string) => {
    setSelectedId(id);
  };

  const handleDownload = (pdfLink: string, excelLink: string) => {
    setSelectedApplication({ pdf_link: pdfLink, excel_link: excelLink });
  };

  const handleDeleteConfirmation = async () => {
    const response = await removeOrders([selectedId]);

    if (!response.error) {
      toast.success("Asset deleted successfully.");
    } else {
      const errorData = response.error as {
        data?: { errors?: { title?: string }[] };
      };
      toast.error(
        errorData?.data?.errors?.[0]?.title || "Something went wrong"
      );
    }

    setSelectedId(null);
  };

  const handleOpenDownloadConfirmation = (value: string) => {
    window.open(value, "_blank");
  };

  useEffect(() => {
    dispatch(setIsApplicationsLoading(isLoading));
  }, [isLoading, dispatch]);

  const showSkeleton = isLoading || isFetching;

  // Calculate the number of columns dynamically
  const getColumnCount = () => {
    let count = 3; // Id, Created Date, Total Items
    count += 1; // Total Value
    if (!isApplicant && !isDashboard) count += 1; // Applicant Email
    if (!isDashboard || isView) count += 1; // Actions
    return count;
  };

  return (
    <div className="relative">
      <ConfirmationDialog
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onConfirm={handleDeleteConfirmation}
        title="Delete Asset"
        message="Are you sure you want to delete this asset?"
        isLoading={isDeleting}
      />
      <ConfirmationDialog
        open={!!selectedApplication}
        onClose={() => setSelectedApplication(null)}
        onConfirm={() => {}}
        handleExcelDownload={() =>
          handleOpenDownloadConfirmation(selectedApplication?.excel_link)
        }
        handleDownloadPDF={() =>
          handleOpenDownloadConfirmation(selectedApplication?.pdf_link)
        }
        title="Download Asset"
        message="Select a file format to download the asset."
        isLoading={isDeleting}
        isDownloadConfirmation={true}
      />
      <Paper
        className="relative pt-1 px-1 flex flex-col flex-auto shadow-1 rounded-lg overflow-hidden w-full h-full"
        elevation={0}
      >
        <div className={clsx(isDashboard ? "h-full" : "h-[calc(100vh-152px)]")}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography className="font-semibold">Id</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className="font-semibold">
                      Created Date
                    </Typography>
                  </TableCell>
                  {!isApplicant && !isDashboard && (
                    <TableCell>
                      <Typography className="font-semibold">
                        Applicant Email
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography className="font-semibold">
                      Total Items
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className="font-semibold">
                      Total Value
                    </Typography>
                  </TableCell>
                  {(!isDashboard || isView) && (
                    <TableCell align="right">
                      <Typography className="font-semibold">Actions</Typography>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {showSkeleton ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="rounded" width={100} height={24} animation="wave" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={90} height={24} animation="wave" /></TableCell>
                      {!isApplicant && !isDashboard && (
                        <TableCell><Skeleton variant="rounded" width={120} height={24} animation="wave" /></TableCell>
                      )}
                      <TableCell><Skeleton variant="rounded" width={60} height={24} animation="wave" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={70} height={24} animation="wave" /></TableCell>
                      {(!isDashboard || isView) && (
                        <TableCell align="right"><Skeleton variant="rounded" width={80} height={24} animation="wave" /></TableCell>
                      )}
                    </TableRow>
                  ))
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getColumnCount()} align="center">
                      <Typography className="text-gray-500 py-8">
                        No assets found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((row: Application) => {
                    const isDisabled = row.is_enabled === 0;

                    const canEdit =
                      !isView &&
                      user?.role === "Applicant" &&
                      ["under_verification", "rejected", ""].includes(
                        row?.application_status || ""
                      ) &&
                      !isDisabled;

                    const canDownload =
                      !isView && user?.role === "Admin" && !isDisabled;

                    const canDelete =
                      !isView &&
                      (row?.application_status === "under_verification" ||
                        !row.application_status) &&
                      !isDisabled;

                    return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        opacity: isDisabled ? 0.6 : 1,
                        cursor: isDisabled ? "not-allowed" : "default",
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ textTransform: "capitalize" }}>
                          {row.application_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.apply_date ? (
                          row.apply_date
                        ) : (
                          <span style={{ marginLeft: "35px" }}>-</span>
                        )}
                      </TableCell>
                      {!isApplicant && !isDashboard && (
                        <TableCell>
                          {row?.applicant?.email ? (
                            <Typography sx={{ textTransform: "capitalize" }}>
                              {row?.applicant?.email || "-"}
                            </Typography>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      )}
                      <TableCell>{row?.total_items || "-"}</TableCell>
                      <TableCell>${row?.sum_current_cost || "0.00"}</TableCell>

                      {(!isDashboard || isView) && (
                        <TableCell align="right">
                          <div className="flex gap-2 justify-end">
                            <Tooltip
                              title={
                                isDisabled
                                  ? "This asset's address is disabled. You can still view its details."
                                  : "View Asset"
                              }
                            >
                              <IconButton
                                aria-label="detail"
                                href={`/apps/applications/${row?.id}${user?.role === "Applicant" ? "?view=true" : ""}`}
                                size="small"
                              >
                                <RemoveRedEye fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                                <Tooltip title="Edit Asset">
                                  <IconButton
                                    aria-label="detail"
                                    href={`/apps/applications/${row?.id}`}
                                    size="small"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            {canDownload && (
                              <Tooltip title="Download Asset">
                                <IconButton
                                  aria-label="detail"
                                  onClick={() =>
                                    handleDownload(
                                      row?.pdf_link,
                                      row?.excel_link
                                    )
                                  }
                                  size="small"
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(!isView &&
                              !isDisabled &&
                              (row?.application_status === "under_verification" ||
                                !row.application_status)) && (
                                <Tooltip title="Delete Asset">
                                  <IconButton
                                    aria-label="detail"
                                    onClick={() => handleDelete(row?.id.toString())}
                                    size="small"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Paper>
    </div>
  );
}

export default ApplicationsTable;